import { inject, Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { VALIDACIONES_IDENTIFICACION } from "@shared/constants/apl-afiliacion.constants";
import { TipoEntidadType } from "@shared/enums/tipo-afiliacion.enum";


@Injectable({
  providedIn: "root",
})
export class AfiliacionesBaseFunctions {
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  constructor(
    private readonly fb: FormBuilder,
  ) { }

  /**
   * Method to get the body for a request based on the form data and the type of entity.
   * @param form FormGroup instance containing the form data.
   * @param type Type of entity.
   * @returns Form body for the request.
   */
  public getBodyToRequest(form: FormGroup, type: string) {
    if (type === TipoEntidadType.ALL) {
      // If the type is 'ALL', call the makeBodyFormFuam method to create the form body for Fuam.
      return this.makeBodyFormFuam(form);
    } else {
      // If the type is not recognized, return null.
      return null;
    }
  }

  /**
   * Method to create a form body for Fuam.
   * @param form FormGroup instance containing the form data.
   * @returns FormGroup instance representing the form body for Fuam.
   */
  private makeBodyFormFuam(form: FormGroup) {
    // Extracting form groups and form array from the provided form
    const datos_empresaBase = form.get("datos_empresa") as FormGroup;
    const datos_empleadoBase = form.get("datos_empleado") as FormGroup;
    const informacion_afiliacionBase = form.get(
      "informacion_afiliacion",
    ) as FormGroup;
    const beneficiariosBase = form.get("beneficiarios") as FormArray;

    // Creating a form group for datos_empresa
    const datos_empresa = this.fb.group({
      id: datos_empresaBase.get("id").value,
      nivel_1_id: datos_empresaBase.get("nivel_1").value ?? null,
      nivel_2_id: datos_empresaBase.get("nivel_2").value ?? null,
      nivel_3_id: datos_empresaBase.get("nivel_3").value ?? null,
      nivel_4_id: datos_empresaBase.get("nivel_4").value ?? null,
      tipo_contrato: datos_empresaBase.get("tipo_contrato").value ?? null,
      tipo_contrato_nombre: datos_empresaBase.get("tipo_contrato_nombre").value ?? null,
      etapa_contrato: datos_empresaBase.get("etapa_contrato").value ?? null,
      fecha_ingreso: datos_empresaBase.get("fecha_ingreso").value ?? null,
      clase_riesgo: datos_empresaBase.get("clase_riesgo").value ?? null,
      departamento_trabajo: datos_empresaBase.get("departamento_trabajo").value ?? null,
      ciudad_trabajo: datos_empresaBase.get("ciudad_trabajo").value ?? null,
      ciudad_trabajo_nombre: datos_empresaBase.get("ciudad_trabajo_label").value ?? null,
      centro_trabajo: datos_empresaBase.get("centro_trabajo").value ?? null,
      centro_trabajo_nombre: datos_empresaBase.get("centro_trabajo_nombre").value ?? null,
      sucursal_nombre: datos_empresaBase.get("sucursal_nombre").value ?? null,
    });

    // Creating a form group for informacion_afiliacion
    const informacion_afiliacion = this.fb.group(
      // Merging values from informacion_afiliacionBase and additional default values
      Object.assign(
        informacion_afiliacionBase.value,
        // Default values for certain fields
        this.fb.group({
          afiliado_ccf_anterior: false,
          identificador: "identificador123",
          fecha_fin: null,
          ingreso_base_de_cotización: "",
          ciudad_trabajo: datos_empresaBase.get("ciudad_trabajo").value ?? null,
          tipo_direccion_trabajo: "",
          direccion_trabajo:
            datos_empleadoBase.get("datos_laborales").get("direccion_laboral")
              .value ?? "",
          departamento_trabajo:
            datos_empresaBase.get("departamento_trabajo").value ?? null,
          municipio_trabajo: "",
          clase_riesgo: datos_empresaBase.get("clase_riesgo").value ?? null,
          centro_trabajo: datos_empresaBase.get("centro_trabajo").value ?? null,
        }).value,
        {},
      ),
    );

    // Assigning datos_empleado and beneficiarios as they are
    const datos_empleado = datos_empleadoBase;
    const beneficiarios = beneficiariosBase;

    // Constructing the final form group and returning it
    return this.fb.group({
      datos_empresa,
      datos_empleado,
      informacion_afiliacion,
      beneficiarios,
    });
  }

  public mostrarNotificacion(mensaje: string, duration: number, horizontalPosition: any = 'right', verticalPosition: any = 'bottom', styles: string | string[] = 'snackbar-green') {
    this.snackBar.open(mensaje, '', {
      duration,
      horizontalPosition,
      verticalPosition,
      panelClass: Array.isArray(styles) ? styles : [styles]
    });
  }

  public calcularValorConIVA = (valor: number, porcentajeIVA: number) => {
    if (valor <= 0) {
      return {
        iva: 0,
        total: 0,
        totalIva: 0
      };
    }

    const tasaIVA = porcentajeIVA / 100;
    const totalIva = valor * tasaIVA;
    const total = valor + totalIva;

    return {
      iva: porcentajeIVA,
      total,
      totalIva
    };
  };

  public aplicarValidaciones(control: AbstractControl, tipo: string): { placeholder: string; hint?: string, min: number, max: number, pattern: string } | null {
    const config = VALIDACIONES_IDENTIFICACION[tipo];
    if (!config) return null;

    control.setValidators([
      Validators.required,
      Validators.minLength(config.min),
      Validators.maxLength(config.max),
      Validators.pattern(config.pattern),
    ]);
    control.updateValueAndValidity();

    return {
      placeholder: config.placeholder,
      hint: config.hint,
      min: config.min,
      max: config.max,
      pattern: config.pattern
    };
  }

  public esTeclaPermitida(tipo: string, key: string): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(key)) return true;

    const config = VALIDACIONES_IDENTIFICACION[tipo];
    if (!config) return true;

    return new RegExp(config.pattern).test(key);
  }

  public esPegadoPermitido(tipo: string, textoPegado: string): boolean {
    const config = VALIDACIONES_IDENTIFICACION[tipo];
    if (!config) return true;

    return new RegExp(config.pattern).test(textoPegado);
  }
  public strictEmailValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(value) ? null : { strictEmail: true };
  }

}
